import {
  and,
  createDB,
  document_classifications,
  documents,
  eq,
  jobs,
} from "@ocr-monorepo/db";
import type { PrimaryDB } from "@ocr-monorepo/db";
import { Effect } from "effect";
import { z } from "zod";
import type {
  ClassificationWorkflowParams,
  FirstStepParams,
  SplitterWorkflowParams,
} from "./index";
import {
  ensureProcessingRun,
  markProcessingDispatchPublished,
  transitionProcessingRun,
  type ProcessingTrigger,
} from "./processing-runs";

export const storedDocumentRouteSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("classify") }),
  z.object({
    kind: z.literal("table"),
    tableId: z.string().min(1),
    tableName: z.string().min(1),
    databaseId: z.string().min(1),
  }),
  z.object({
    kind: z.literal("splitter"),
    splitterId: z.string().min(1),
    splitterVersionId: z.string().min(1),
    testOnly: z.boolean().default(false),
  }),
  z.object({ kind: z.literal("deferred"), tableId: z.string().nullable().optional() }),
]);

export type StoredDocumentRoute = z.infer<typeof storedDocumentRouteSchema>;

export type DocumentIngestionWorkflowService = {
  createClassificationWorkflow(
    params: ClassificationWorkflowParams,
  ): Promise<{ id: string; status?: string }>;
  createOcrWorkflow(
    params: FirstStepParams,
  ): Promise<{ id: string; status?: string }>;
  createSplitterWorkflow(
    params: SplitterWorkflowParams,
  ): Promise<{ id: string; status?: string }>;
};

export type PersistStoredDocumentInput = {
  fileId: string;
  uploadId?: string | null;
  name: string;
  userId: string;
  projectId: number;
  fileSize: number;
  pageCount?: number | null;
  ingestionSource?: string | null;
  ingestionSourceId?: string | null;
  usageReservationId?: string;
  route: StoredDocumentRoute;
};

export type PersistedDocument = {
  id: number;
  inserted: boolean;
  fileId: string;
  route: StoredDocumentRoute;
};

export type DispatchedDocument = {
  workflowId: string | null;
  jobId: number | null;
  runId: string | null;
  configSnapshotId: string | null;
};

function processingTrigger(source?: string | null): ProcessingTrigger {
  if (source === "email") return "email";
  if (source === "api") return "api";
  if (source === "connector") return "connector";
  return "upload";
}

function initialDocumentStatus(route: StoredDocumentRoute): string {
  if (route.kind === "classify") return "classifying";
  return "queued";
}

export async function persistStoredDocument(
  db: PrimaryDB,
  input: PersistStoredDocumentInput,
): Promise<PersistedDocument> {
  const route = storedDocumentRouteSchema.parse(input.route);
  const values: typeof documents.$inferInsert = {
    file_id: input.fileId,
    upload_id: input.uploadId ?? null,
    ingestion_source: input.ingestionSource ?? null,
    ingestion_source_id: input.ingestionSourceId ?? null,
    name: input.name,
    user_id: input.userId,
    project_id: input.projectId,
    password_protection_check_enabled: false,
    password_protection_status: "unencrypted",
    table_id:
      route.kind === "table"
        ? route.tableId
        : route.kind === "deferred"
          ? (route.tableId ?? null)
          : null,
    status: initialDocumentStatus(route),
    page_count: input.pageCount ?? null,
    file_size: input.fileSize,
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  const inserted = input.ingestionSourceId
    ? await db
        .insert(documents)
        .values(values)
        .onConflictDoNothing({ target: documents.ingestion_source_id })
        .returning({ id: documents.id })
    : await db.insert(documents).values(values).returning({ id: documents.id });

  let documentId = inserted[0]?.id;
  if (!documentId && input.ingestionSourceId) {
    const [existing] = await db
      .select({ id: documents.id })
      .from(documents)
      .where(
        and(
          eq(documents.ingestion_source_id, input.ingestionSourceId),
          eq(documents.project_id, input.projectId),
          eq(documents.user_id, input.userId),
        ),
      )
      .limit(1);
    documentId = existing?.id;
  }

  if (!documentId) {
    throw new Error("Unable to persist document");
  }

  return {
    id: documentId,
    inserted: inserted.length > 0,
    fileId: input.fileId,
    route,
  };
}

export async function dispatchStoredDocument(
  db: PrimaryDB,
  workflow: DocumentIngestionWorkflowService,
  document: PersistedDocument,
  input: Pick<
    PersistStoredDocumentInput,
    "userId" | "projectId" | "usageReservationId" | "ingestionSource"
  > & { dispatchKey?: string },
): Promise<DispatchedDocument> {
  const route = document.route;
  if (route.kind === "deferred") {
    return {
      workflowId: null,
      jobId: null,
      runId: null,
      configSnapshotId: null,
    };
  }

  const dispatchKey = input.dispatchKey ?? "initial";
  const run = await Effect.runPromise(Effect.tryPromise({
    try: () => ensureProcessingRun(db, {
    documentId: document.id,
    userId: input.userId,
    projectId: input.projectId,
    trigger: processingTrigger(input.ingestionSource),
    route: route.kind === "classify"
        ? { kind: "automatic" }
        : route.kind === "splitter"
          ? {
              kind: "splitter",
              splitterId: route.splitterId,
              splitterVersionId: route.splitterVersionId,
              testOnly: route.testOnly,
            }
        : {
            kind: "table",
            tableId: route.tableId,
            tableName: route.tableName,
            destinationId: route.databaseId,
          },
    idempotencyKey: `document-processing:${document.id}:${dispatchKey}`,
    usageReservationId: input.usageReservationId,
  }),
    catch: (error) => error instanceof Error ? error : new Error(String(error)),
  }));

  await Effect.runPromise(
    Effect.tryPromise({
      try: () =>
        db
          .update(documents)
          .set({
            status: initialDocumentStatus(route),
            updatedAt: new Date(),
          })
          .where(eq(documents.id, document.id)),
      catch: (error) =>
        error instanceof Error ? error : new Error(String(error)),
    }),
  );

  if (route.kind === "splitter") {
    const result = await Effect.runPromise(
      Effect.tryPromise({
        try: () => workflow.createSplitterWorkflow({
          documentId: document.id,
          fileId: document.fileId,
          projectId: input.projectId,
          userId: input.userId,
          splitterId: route.splitterId,
          splitterVersionId: route.splitterVersionId,
          runId: run.runId,
          configSnapshotId: run.configSnapshotId,
          testOnly: route.testOnly,
        }),
        catch: (error) => error instanceof Error ? error : new Error(String(error)),
      }),
    );
    await markProcessingDispatchPublished(db, {
      runId: run.runId,
      workflowInstanceId: result.id,
      stage: "understand",
    });
    return {
      workflowId: result.id,
      jobId: null,
      runId: run.runId,
      configSnapshotId: run.configSnapshotId,
    };
  }

  if (route.kind === "classify") {
    try {
      const result = await workflow.createClassificationWorkflow({
        documentId: document.id,
        fileId: document.fileId,
        projectId: input.projectId,
        userId: input.userId,
        usageReservationId: input.usageReservationId,
        runId: run.runId,
        configSnapshotId: run.configSnapshotId,
      });
      await markProcessingDispatchPublished(db, {
        runId: run.runId,
        workflowInstanceId: result.id,
        stage: "route",
      });
      return {
        workflowId: result.id,
        jobId: null,
        runId: run.runId,
        configSnapshotId: run.configSnapshotId,
      };
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Unable to start automatic routing";
      await db.batch([
        db
          .update(documents)
          .set({ status: "unclassified", updatedAt: new Date() })
          .where(eq(documents.id, document.id)),
        db.insert(document_classifications).values({
          document_id: document.id,
          workflow_instance_id: null,
          matched_classifier_id: null,
          confidence_score: null,
          classification_reasoning: null,
          all_scores: [],
          status: "error",
          error_message: message,
          processing_duration_ms: 0,
          user_id: input.userId,
          project_id: input.projectId,
        }),
      ]);
      throw error;
    }
  }

  const jobIdempotencyKey = input.dispatchKey
    ? `document-ingestion:${document.id}:${dispatchKey}`
    : `document-ingestion:${document.id}`;
  const insertedJobs = await db
    .insert(jobs)
    .values({
      table_id: route.tableId,
      projects_id: input.projectId,
      user_id: input.userId,
      workflow_type: "insert-workflow",
      jobStatus: "queued",
      idempotency_key: jobIdempotencyKey,
    })
    .onConflictDoNothing({ target: jobs.idempotency_key })
    .returning({ id: jobs.id });

  let jobId = insertedJobs[0]?.id;
  if (!jobId) {
    const [existingJob] = await db
      .select({ id: jobs.id })
      .from(jobs)
      .where(eq(jobs.idempotency_key, jobIdempotencyKey))
      .limit(1);
    jobId = existingJob?.id;
  }
  if (!jobId) throw new Error("Unable to prepare document extraction");

  await transitionProcessingRun(db, {
    runId: run.runId,
    status: "queued",
    stage: "admit",
    jobId,
  });

  try {
    const result = await workflow.createOcrWorkflow({
      table: route.tableName,
      tableId: route.tableId,
      databaseId: route.databaseId,
      documents: [document.fileId],
      fileIds: [document.fileId],
      userId: input.userId,
      projectId: input.projectId,
      jobId,
      processingMode: "instant",
      idempotencyKey: input.dispatchKey
        ? `document-ingestion-${document.id}-${dispatchKey}`
        : `document-ingestion-${document.id}`,
      usageReservationId: input.usageReservationId,
      runId: run.runId,
      configSnapshotId: run.configSnapshotId,
    });
    await markProcessingDispatchPublished(db, {
      runId: run.runId,
      workflowInstanceId: result.id,
      stage: "extract",
    });
    return {
      workflowId: result.id,
      jobId,
      runId: run.runId,
      configSnapshotId: run.configSnapshotId,
    };
  } catch (error) {
    await db.batch([
      db
        .update(documents)
        .set({ status: "failed", updatedAt: new Date() })
        .where(eq(documents.id, document.id)),
      db
        .update(jobs)
        .set({ jobStatus: "failed", updatedAt: new Date() })
        .where(eq(jobs.id, jobId)),
    ]);
    throw error;
  }
}

export async function ingestStoredDocument(
  binding: D1Database,
  workflow: DocumentIngestionWorkflowService,
  input: PersistStoredDocumentInput,
): Promise<
  PersistedDocument & { workflowId: string | null; jobId: number | null }
> {
  return Effect.runPromise(
    Effect.gen(function* () {
      const db = createDB(binding);
      const document = yield* Effect.tryPromise({
        try: () => persistStoredDocument(db, input),
        catch: (error) => error instanceof Error ? error : new Error(String(error)),
      });
      const dispatched = yield* Effect.tryPromise({
        try: () => dispatchStoredDocument(db, workflow, document, input),
        catch: (error) => error instanceof Error ? error : new Error(String(error)),
      });
      return { ...document, ...dispatched };
    }),
  );
}
